import { DomainError } from '../shared/DomainError';
import type { ExerciseLog } from './ExerciseLog';
import type { CatalogExercise } from './ExerciseCatalog';
import { fromExerciseLogData, toExerciseLogData, type ExerciseLogContent } from './WorkoutContent';
import { ExerciseStatus, WorkoutSessionStatus } from './enums';

export type ExerciseTransition = 'complete' | 'skip' | 'reset';

function requireContent(log: ExerciseLog): ExerciseLogContent {
  const content = fromExerciseLogData(log.data);
  if (!content) {
    throw new DomainError('invalid_exercise_content', 'Le contenu de cet exercice est invalide.');
  }
  return content;
}

function withoutWeight(content: ExerciseLogContent): ExerciseLogContent {
  return {
    catalogId: content.catalogId,
    name: content.name,
    equipment: content.equipment,
    unit: content.unit,
    targetValue: content.targetValue,
    instructions: content.instructions,
    supportsWeight: content.supportsWeight,
    status: content.status,
    ...(content.replacementCatalogId ? { replacementCatalogId: content.replacementCatalogId } : {}),
    ...(content.replacedWithCatalogId ? { replacedWithCatalogId: content.replacedWithCatalogId } : {}),
  };
}

export function transitionExerciseLog(
  log: ExerciseLog,
  transition: ExerciseTransition,
  timestamp: string,
  options?: { readonly userWeight?: number; readonly weightUnit?: 'kg' | 'lb' },
): ExerciseLog {
  const content = requireContent(log);
  switch (transition) {
    case 'complete': {
      if (content.status !== ExerciseStatus.pending) {
        throw new DomainError('invalid_exercise_transition', 'Cet exercice ne peut pas être terminé.');
      }
      const updated: ExerciseLogContent = {
        ...content,
        status: ExerciseStatus.completed,
        ...(content.supportsWeight && options?.userWeight !== undefined
          ? { userWeight: options.userWeight }
          : {}),
        ...(content.supportsWeight && options?.weightUnit !== undefined
          ? { weightUnit: options.weightUnit }
          : {}),
      };
      return { ...log, data: toExerciseLogData(updated), updatedAt: timestamp };
    }
    case 'skip': {
      if (content.status !== ExerciseStatus.pending) {
        throw new DomainError('invalid_exercise_transition', 'Cet exercice ne peut pas être ignoré.');
      }
      const updated: ExerciseLogContent = { ...content, status: ExerciseStatus.skipped };
      return { ...log, data: toExerciseLogData(updated), updatedAt: timestamp };
    }
    case 'reset': {
      if (content.status === ExerciseStatus.pending) {
        throw new DomainError('invalid_exercise_transition', 'Cet exercice est déjà à faire.');
      }
      const updated: ExerciseLogContent = { ...withoutWeight(content), status: ExerciseStatus.pending };
      return { ...log, data: toExerciseLogData(updated), updatedAt: timestamp };
    }
  }
}

/** Swaps the prescribed exercise for its equipment-free alternative and resets it to pending. */
export function replaceExerciseLog(
  log: ExerciseLog,
  replacement: CatalogExercise,
  timestamp: string,
): ExerciseLog {
  const content = requireContent(log);
  if (content.status !== ExerciseStatus.pending) {
    throw new DomainError('invalid_exercise_transition', 'Cet exercice ne peut pas être remplacé.');
  }
  if (content.replacementCatalogId !== replacement.id) {
    throw new DomainError(
      'invalid_exercise_replacement',
      'Ce remplacement ne correspond pas à l’exercice prescrit.',
    );
  }
  const updated: ExerciseLogContent = {
    catalogId: replacement.id,
    name: replacement.name,
    equipment: replacement.equipment,
    unit: replacement.unit,
    targetValue: replacement.targetValue,
    instructions: replacement.instructions,
    supportsWeight: replacement.supportsWeight,
    status: ExerciseStatus.replaced,
    replacedWithCatalogId: content.catalogId,
  };
  return { ...log, data: toExerciseLogData(updated), updatedAt: timestamp };
}

/**
 * A session already marked `reduced` stays reduced — it is a terminal, user-chosen outcome,
 * never silently overwritten by a later individual exercise transition.
 */
export function deriveSessionStatus(
  currentStatus: WorkoutSessionStatus,
  exerciseLogs: readonly ExerciseLog[],
): WorkoutSessionStatus {
  if (currentStatus === WorkoutSessionStatus.reduced) return WorkoutSessionStatus.reduced;
  const contents = exerciseLogs.map(requireContent);
  const resolved = contents.filter((content) => content.status !== ExerciseStatus.pending);
  if (resolved.length === 0) return WorkoutSessionStatus.pending;
  if (resolved.length === contents.length) return WorkoutSessionStatus.completed;
  return WorkoutSessionStatus.inProgress;
}
